import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, Image as ImageIcon, Loader2, Plus, X } from "lucide-react";
import { decodeBarcode } from "@/lib/barcode";

export const Route = createFileRoute("/upload/$token")({
  component: UploadPage,
});

interface TokenInfo {
  target_type: string;
  target_id: number;
}

interface CapturedPhoto {
  url: string;
  gtin: string | null;
}

function UploadPage() {
  const { token } = Route.useParams();
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Validate before the operator takes a photo — discovering an expired link
  // only after framing the shot is a bad trade for one request.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/upload/token/${token}`)
      .then(async (res) => {
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) setTokenError(data.error ?? "Invalid or expired link");
        else setTokenInfo(data as TokenInfo);
      })
      .catch(() => {
        if (!cancelled) setTokenError("Could not reach the server");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (tokenError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center gap-3">
        <X className="h-10 w-10 text-red-600" />
        <p className="text-sm font-semibold text-red-600">{tokenError}</p>
        <p className="text-xs text-muted-foreground">
          Ask for a fresh QR code and scan it again.
        </p>
      </div>
    );
  }

  if (!tokenInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return tokenInfo.target_type === "inventory_intake" ? (
    <IntakeCapture token={token} />
  ) : (
    <SinglePhotoCapture token={token} />
  );
}

async function uploadPhoto(
  token: string,
  file: File,
  extraFields: Record<string, string> = {},
) {
  // Every field must be appended BEFORE the file. Multipart bodies are parsed
  // as a stream, so a field that follows the file part isn't available to the
  // server until the file has been consumed — the server reads them up front.
  const formData = new FormData();
  formData.append("token", token);
  for (const [k, v] of Object.entries(extraFields)) formData.append(k, v);
  formData.append("file", file);

  const res = await fetch("/api/upload", { method: "POST", body: formData });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Upload failed");
  return data as { file_storage_id: number };
}

// ── Inventory intake: many containers, several photos each ───────────────────

interface IntakeItem {
  draft_id: number;
  /** 1-based capture order, stable as earlier containers get committed. */
  position: number;
  status: string;
  photo_count: number;
  label: string | null;
  can_capture: boolean;
}

interface IntakeSessionView {
  active_draft_id: number | null;
  items: IntakeItem[];
}

/**
 * The containers captured so far, polled.
 *
 * Polled rather than pushed because the phone is not the only writer: the
 * desktop can aim capture at a container, and extraction fills in the product
 * name a few seconds after a jar is finished. Ten seconds is deliberately slow —
 * nothing here is time-critical, the payload carries no signed image URLs, and
 * the screen is a phone in someone's hand between jars.
 */
function useIntakeSessionView(token: string) {
  const [view, setView] = useState<IntakeSessionView>({
    active_draft_id: null,
    items: [],
  });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/upload/intake/session/${token}`);
      if (res.ok) setView((await res.json()) as IntakeSessionView);
    } catch {
      // The capture buttons keep working without this; it only drives the
      // "go back to an earlier item" affordances.
    }
  }, [token]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  return {
    items: view.items,
    activeDraftId: view.active_draft_id,
    refresh,
  };
}

function IntakeCapture({ token }: { token: string }) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const [current, setCurrent] = useState<CapturedPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Which earlier container the operator has gone back to, if any. Null is the
  // ordinary forward path: each new photo either continues the container being
  // shot or starts the next one.
  const [resumingDraftId, setResumingDraftId] = useState<number | null>(null);

  const session = useIntakeSessionView(token);

  // The phone decides which photos belong to one container, because the person
  // holding the jar knows and the server cannot tell. `start_new` is that
  // signal; it is only true for the first photo of a container.
  const startNewRef = useRef(true);

  const resuming = resumingDraftId
    ? session.items.find((i) => i.draft_id === resumingDraftId) ?? null
    : null;

  // Containers still open to more photos, newest first — the one just finished
  // is the one most likely to be missing a panel.
  const reopenable = session.items
    .filter((i) => i.can_capture && i.draft_id !== resumingDraftId)
    .reverse();

  // Only offered while nothing is half-shot: acting on it mid-container would
  // silently split the jar in front of the operator across two records.
  const idle = current.length === 0 && resumingDraftId == null;
  const steered =
    idle && session.activeDraftId != null
      ? session.items.find((i) => i.draft_id === session.activeDraftId) ?? null
      : null;

  async function targetDraft(draftId: number | null) {
    setError(null);
    try {
      const res = await fetch("/api/upload/intake/target", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, draft_id: draftId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error ?? "Could not switch item");
      }
      setResumingDraftId(draftId);
      startNewRef.current = draftId == null;
      await session.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not switch item");
    }
  }

  // Revoke object URLs on unmount so a long session doesn't leak.
  useEffect(() => {
    return () => {
      for (const p of current) URL.revokeObjectURL(p.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    // Sequential, not parallel: the first upload of a container carries
    // start_new and opens the draft, so the rest must land after it or they
    // would each open one of their own.
    for (const file of files) {
      const localUrl = URL.createObjectURL(file);
      try {
        // A barcode is a hard identity key — far better than any OCR name
        // match, and free to read here where we have the full-resolution frame.
        const gtin = await decodeBarcode(file);

        await uploadPhoto(token, file, {
          // Naming the draft outright, rather than relying on the server's
          // "newest open container", is the only way to reach one the operator
          // has already moved past.
          ...(resumingDraftId != null
            ? { draft_id: String(resumingDraftId), start_new: "false" }
            : { start_new: String(startNewRef.current) }),
          ...(gtin ? { gtin } : {}),
        });

        startNewRef.current = false;
        setCurrent((prev) => [...prev, { url: localUrl, gtin }]);
      } catch (err) {
        URL.revokeObjectURL(localUrl);
        setError(err instanceof Error ? err.message : "Upload failed");
        break;
      }
    }

    setUploading(false);
  }

  async function nextItem() {
    const finishing = resumingDraftId;
    for (const p of current) URL.revokeObjectURL(p.url);
    setCurrent([]);
    setResumingDraftId(null);
    startNewRef.current = true;

    // Tell the server now, so the desktop shows the finished item and starts
    // reading its label immediately rather than waiting for the next photo.
    // Best-effort when closing the container being shot: the next upload still
    // carries start_new, so a failure there costs promptness, not correctness.
    try {
      await fetch("/api/upload/intake/next-item", {
        method: "POST",
        headers: { "content-type": "application/json" },
        // A re-opened container is not the newest one, so it has to say which
        // it is — "whatever is open" would close the wrong jar.
        body: JSON.stringify({
          token,
          ...(finishing != null ? { draft_id: finishing } : {}),
        }),
      });
    } catch {
      /* ignore — start_new on the next upload covers it */
    }
    await session.refresh();
  }

  const hasCurrent = current.length > 0;
  const itemCount = session.items.length;

  return (
    <div className="flex flex-col min-h-screen px-5 py-6 gap-5">
      <div className="flex items-baseline justify-between">
        <h1 className="text-base font-semibold">Inventory intake</h1>
        <span className="text-xs text-muted-foreground">
          {itemCount} {itemCount === 1 ? "item" : "items"}
        </span>
      </div>

      {/* The desktop has the photos in front of it and can see what is missing,
          but no camera — so it aims the person who does have one. */}
      {steered && (
        <div className="flex flex-col gap-2 border-2 border-warning bg-yellow-2 p-3">
          <p className="text-sm font-semibold">
            More photos wanted for item {steered.position}
            {steered.label ? ` — ${steered.label}` : ""}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => targetDraft(steered.draft_id)}
              className="flex-1 py-2 bg-primary text-primary-foreground text-sm font-medium"
            >
              Photograph it
            </button>
            <button
              type="button"
              onClick={() => targetDraft(null)}
              className="px-4 py-2 border border-olive-7 text-sm text-olive-11"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {resuming && (
        <div className="flex items-center justify-between gap-3 border border-olive-7 bg-olive-3 px-3 py-2">
          <span className="text-sm">
            Adding to item {resuming.position}
            {resuming.label ? ` — ${resuming.label}` : ""}
          </span>
          <button
            type="button"
            disabled={uploading}
            onClick={() => targetDraft(null)}
            className="text-xs text-olive-11 underline disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      {hasCurrent && (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">
            {resuming ? `Item ${resuming.position}` : "Current item"} —{" "}
            {current.length} {current.length === 1 ? "photo" : "photos"} added
          </span>
          <div className="flex gap-2 overflow-x-auto">
            {current.map((photo, i) => (
              <div key={i} className="relative shrink-0">
                <img
                  src={photo.url}
                  alt={`Photo ${i + 1}`}
                  className="h-20 w-20 object-cover border border-olive-6"
                />
                {photo.gtin && (
                  <span className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[9px] text-center py-0.5">
                    barcode
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two inputs, because `capture` forces the camera and cannot be
          toggled per-click: one goes straight to the camera, one opens the
          photo library / file picker (and takes several at once). */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFiles}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFiles}
      />

      <button
        type="button"
        disabled={uploading}
        onClick={() => cameraInputRef.current?.click()}
        className="flex flex-col items-center justify-center gap-3 w-full py-10 border-2 border-dashed border-olive-7 active:bg-olive-3 disabled:opacity-50 transition-colors"
      >
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : (
          <Camera className="h-8 w-8 text-olive-9" />
        )}
        <span className="text-sm font-medium text-olive-11">
          {uploading
            ? "Uploading…"
            : hasCurrent
              ? "Add another photo of this item"
              : "Photograph the label"}
        </span>
        {hasCurrent && !uploading && (
          <span className="text-xs text-muted-foreground px-6">
            Add the nutrition panel or ingredient list if there is one
          </span>
        )}
      </button>

      <button
        type="button"
        disabled={uploading}
        onClick={() => libraryInputRef.current?.click()}
        className="flex items-center justify-center gap-2 w-full py-3 border border-olive-7 active:bg-olive-3 disabled:opacity-50 text-sm text-olive-11 transition-colors"
      >
        <ImageIcon className="h-4 w-4" />
        Choose existing photos
      </button>

      {hasCurrent && (
        <button
          type="button"
          disabled={uploading}
          onClick={nextItem}
          className="flex items-center justify-center gap-2 w-full py-4 bg-primary text-primary-foreground disabled:opacity-50 text-sm font-medium"
        >
          {resuming ? (
            <Check className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {resuming ? "Done adding" : "Next item"}
        </button>
      )}

      {error && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}

      {/* Going back to a container already moved past. Offered whenever one is
          open — including when it is the only one, which is the common case:
          the operator taps "Next item", then remembers the nutrition panel. */}
      {idle && reopenable.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">
            Add more photos to an earlier item
          </span>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {reopenable.map((item) => (
              <button
                key={item.draft_id}
                type="button"
                onClick={() => targetDraft(item.draft_id)}
                className="flex flex-col items-start gap-0.5 shrink-0 border border-olive-7 active:bg-olive-3 px-3 py-2 text-left min-w-24"
              >
                <span className="text-sm font-medium">
                  Item {item.position}
                </span>
                <span className="text-xs text-muted-foreground max-w-32 truncate">
                  {item.label ?? `${item.photo_count} photo${item.photo_count === 1 ? "" : "s"}`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center mt-auto">
        Keep this page open. Review and confirm on the computer.
      </p>
    </div>
  );
}

// ── Every other consumer: one photo, then done ───────────────────────────────

function SinglePhotoCapture({ token }: { token: string }) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<"ready" | "uploading" | "done" | "error">(
    "ready",
  );
  const [errorMessage, setErrorMessage] = useState("");

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus("uploading");
    try {
      await uploadPhoto(token, file);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Upload failed");
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center gap-4">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />

      {status === "ready" && (
        <>
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="flex flex-col items-center justify-center gap-3 w-48 h-48 rounded-2xl border-2 border-dashed border-olive-7 hover:border-olive-9 active:bg-olive-3 transition-colors"
          >
            <Camera className="h-10 w-10 text-olive-9" />
            <span className="text-sm font-medium text-olive-11">Take Photo</span>
          </button>
          <button
            type="button"
            onClick={() => libraryInputRef.current?.click()}
            className="flex items-center gap-2 text-sm text-olive-11 underline"
          >
            <ImageIcon className="h-4 w-4" />
            Choose an existing photo
          </button>
        </>
      )}

      {status === "uploading" && (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Uploading...</p>
        </>
      )}

      {status === "done" && (
        <>
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-sm font-semibold text-green-700">Photo uploaded</p>
          <p className="text-xs text-muted-foreground">You can close this page.</p>
        </>
      )}

      {status === "error" && (
        <>
          <p className="text-sm font-semibold text-red-600">
            {errorMessage || "Something went wrong"}
          </p>
          <button
            type="button"
            onClick={() => setStatus("ready")}
            className="text-sm underline text-muted-foreground"
          >
            Try again
          </button>
        </>
      )}
    </div>
  );
}
